import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Constrants from "../constrants/constrant";
import { useToast } from "../context/ToastProvider";
import { Link } from "react-router-dom";
import Transactions from "../components/profile/Transactions";

function Profile() {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [activeTab, setActiveTab] = useState("profile");
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    full_name: "",
    date_of_birth: "",
    gender: "",
    address: "",
    country: "",
    state: "",
    phone_number: "",
    bio: "",
  });

  // Password change states
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    new_password_confirmation: "",
  });

  // Additional data states
  const [favorites, setFavorites] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [apps, setApps] = useState([]);

  useEffect(() => {
    checkAuth();
    fetchProfileData();
  }, []);

  const checkAuth = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      addToast("Please login to view your profile", "error");
      navigate("/login");
    }
  };

  const fetchProfileData = async () => {
    try {
      const token = localStorage.getItem("token");
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const res = await Constrants.get("/profile", config);

      setUser(res.data.user);
      setProfile(res.data.profile);

      // Set form data
      setFormData({
        name: res.data.user.name || "",
        email: res.data.user.email || "",
        full_name: res.data.profile?.full_name || "",
        date_of_birth: res.data.profile?.date_of_birth || "",
        gender: res.data.profile?.gender || "",
        address: res.data.profile?.address || "",
        country: res.data.profile?.country || "",
        state: res.data.profile?.state || "",
        phone_number: res.data.profile?.phone_number || "",
        bio: res.data.profile?.bio || "",
      });

      setLoading(false);
    } catch (error) {
      console.error("Error fetching profile:", error);
      addToast("Failed to load profile data", "error");
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem("token");
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      // Update basic info
      await Constrants.put("/profile/update", {
        name: formData.name,
        email: formData.email
      }, config);

      // Update profile details
      await Constrants.put("/profile/details", {
        full_name: formData.full_name,
        date_of_birth: formData.date_of_birth,
        gender: formData.gender,
        address: formData.address,
        country: formData.country,
        state: formData.state,
        phone_number: formData.phone_number,
        bio: formData.bio,
      }, config);

      // Update localStorage
      const updatedUser = { ...user, name: formData.name, email: formData.email };
      localStorage.setItem("user", JSON.stringify(updatedUser));

      addToast("Profile updated successfully!", "success");
      setIsEditing(false);
      fetchProfileData();
    } catch (error) {
      console.error("Error updating profile:", error);
      addToast(error.response?.data?.message || "Failed to update profile", "error");
    }
  };

  const handleUpdateAvatar = async () => {
    if (!avatarFile) return;

    try {
      const token = localStorage.getItem("token");
      const formDataAvatar = new FormData();
      formDataAvatar.append("avatar", avatarFile);

      const res = await Constrants.post("/profile/avatar", formDataAvatar, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      addToast("Avatar updated successfully!", "success");
      setAvatarFile(null);
      setAvatarPreview(null);
      fetchProfileData();
    } catch (error) {
      console.error("Error updating avatar:", error);
      addToast("Failed to update avatar", "error");
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwordData.new_password !== passwordData.new_password_confirmation) {
      addToast("Passwords do not match", "error");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await Constrants.put("/profile/password", passwordData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      addToast("Password changed successfully!", "success");
      setPasswordData({
        current_password: "",
        new_password: "",
        new_password_confirmation: "",
      });
      setActiveTab("profile");
    } catch (error) {
      console.error("Error changing password:", error);
      addToast(error.response?.data?.message || "Failed to change password", "error");
    }
  };

  const fetchFavorites = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await Constrants.get("/profile/favorites", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(res.data.favorites);
    } catch (error) {
      console.error("Error fetching favorites:", error);
    }
  };

  const fetchReviews = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await Constrants.get("/profile/reviews", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviews(res.data.reviews);
    } catch (error) {
      console.error("Error fetching reviews:", error);
    }
  };

  const fetchApps = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await Constrants.get("/profile/apps", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApps(res.data.apps);
    } catch (error) {
      console.error("Error fetching apps:", error);
    }
  };

  useEffect(() => {
    if (activeTab === "favorites") fetchFavorites();
    if (activeTab === "reviews") fetchReviews();
    if (activeTab === "apps") fetchApps();
  }, [activeTab]);

  if (loading)
    return (
      <div style={styles.loaderContainer}>
        <div style={styles.spinner}></div>
      </div>
    );

  return (
    <div className="profile-container">
      <div className="profile-header">
        <div className="profile-avatar-section">
          <img src={user?.avatar || "/default-avatar.png"} alt="Profile" className="profile-avatar-large" />
          <div className="avatar-upload">
            <input
              type="file"
              id="avatar-input"
              accept="image/*"
              onChange={handleAvatarChange}
              style={{ display: "none" }}
            />
            <label htmlFor="avatar-input" className="avatar-upload-btn">
              Change Photo
            </label>
            {avatarFile && (
              <button onClick={handleUpdateAvatar} className="avatar-save-btn">
                Save Photo
              </button>
            )}
          </div>
        </div>
        <div className="profile-info">
          <h1>{user?.name}</h1>
          <p className="profile-email">{user?.email}</p>
          <span className="profile-role">{user?.role}</span>
        </div>
      </div>

      <div className="profile-tabs">
        <button
          className={`tab-btn ${activeTab === "profile" ? "active" : ""}`}
          onClick={() => setActiveTab("profile")}
        >
          Profile
        </button>
        <button
          className={`tab-btn ${activeTab === "security" ? "active" : ""}`}
          onClick={() => setActiveTab("security")}
        >
          Security
        </button>
        {/* <button 
          className={`tab-btn ${activeTab === "favorites" ? "active" : ""}`}
          onClick={() => setActiveTab("favorites")}
        >
          Favorites
        </button> */}
        <button
          className={`tab-btn ${activeTab === "reviews" ? "active" : ""}`}
          onClick={() => setActiveTab("reviews")}
        >
          Reviews
        </button>
        <button
          className={`tab-btn ${activeTab === "purchases" ? "active" : ""}`}
          onClick={() => setActiveTab("purchases")}
        >
          My Purchases
        </button>
        {user?.role === "developer" && (
          <button
            className={`tab-btn ${activeTab === "apps" ? "active" : ""}`}
            onClick={() => setActiveTab("apps")}
          >
            My Apps
          </button>
        )}
      </div>

      <div className="profile-content">
        {activeTab === "profile" && (
          <div className="profile-tab-content">
            <div className="content-header">
              <h2>Profile Information</h2>
              {!isEditing ? (
                <button onClick={() => setIsEditing(true)} className="edit-btn">
                  Edit Profile
                </button>
              ) : (
                <div className="edit-actions">
                  <button onClick={handleUpdateProfile} className="save-btn">
                    Save Changes
                  </button>
                  <button onClick={() => setIsEditing(false)} className="cancel-btn">
                    Cancel
                  </button>
                </div>
              )}
            </div>

            <form className="profile-form" onSubmit={handleUpdateProfile}>
              <div className="form-row">
                <div className="form-group">
                  <label>Username</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                  />
                </div>
                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date of Birth</label>
                  <input
                    type="date"
                    name="date_of_birth"
                    value={formData.date_of_birth}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                  />
                </div>
                <div className="form-group">
                  <label>Gender</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Country</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                  />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Bio</label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  rows="4"
                  placeholder="Tell us about yourself..."
                />
              </div>
            </form>
          </div>
        )}

        {activeTab === "security" && (
          <div className="profile-tab-content">
            <h2>Change Password</h2>
            <form className="profile-form" onSubmit={handleChangePassword}>
              <div className="form-group">
                <label>Current Password</label>
                <input
                  type="password"
                  name="current_password"
                  value={passwordData.current_password}
                  onChange={handlePasswordChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  name="new_password"
                  value={passwordData.new_password}
                  onChange={handlePasswordChange}
                  minLength="8"
                  required
                />
              </div>
              <div className="form-group">
                <label>Confirm New Password</label>
                <input
                  type="password"
                  name="new_password_confirmation"
                  value={passwordData.new_password_confirmation}
                  onChange={handlePasswordChange}
                  minLength="8"
                  required
                />
              </div>
              <button type="submit" className="save-btn">
                Change Password
              </button>
            </form>
          </div>
        )}

        {activeTab === "favorites" && (
          <div className="profile-tab-content">
            <h2>Favorite Apps</h2>
            {favorites.length === 0 ? (
              <p className="empty-message">No favorite apps yet</p>
            ) : (
              <div className="apps-grid">
                {favorites.map((fav) => (
                  <Link to={`/app/details/${fav.app.slug}`} key={fav.app.id} className="game-card horizontal-card" style={{ textDecoration: "none" }}>
                    <div
                      className="game-image"
                      style={{
                        background: "linear-gradient(45deg, #667eea, #764ba2)",
                        backgroundImage: `url(${fav.app.feature_graphic_url ? fav.app.feature_graphic_url : fav.app.icon_url})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        borderRadius: "10px",
                      }}
                    >
                      <div
                        className="game-icon"
                        style={{
                          backgroundImage: `url(${fav.app.icon_url})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                        }}
                      ></div>
                    </div>
                    <div className="game-info">
                      <div className="game-title">{fav.app.name}</div>
                      <div className="game-developer">{fav.app.developer_name}</div>
                      <div className="game-rating">
                        <span className="stars">★★★★☆</span>
                        <span className="rating-text">{fav.app.rating}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "reviews" && (
          <div className="reviews-container">
            <h2 className="reviews-title">My Reviews</h2>

            {reviews.length === 0 ? (
              <div className="reviews-empty">
                <div className="empty-icon">💬</div>
                <p className="empty-text">You haven’t reviewed any apps yet</p>
                <p className="empty-subtext">Start exploring and share your thoughts!</p>
              </div>
            ) : (
              <div className="reviews-grid">
                {reviews.map((review) => (
                  <div key={review.id} className="review-card">
                    <img
                      src={review.app?.icon_url || "/default-icon.png"}
                      alt={review.app?.name || "App Icon"}
                      className="app-icon"
                    />
                    <div className="review-content">
                      <a href={`/app/details/${review.app.slug}`} className="app-name">
                        {review.app?.name}
                      </a>

                      {review.rating && (
                        <div className="review-rating">
                          {[...Array(5)].map((_, i) => (
                            <span
                              key={i}
                              className={`star ${i < review.rating ? "filled" : ""}`}
                            >
                              ★
                            </span>
                          ))}
                          <span className="rating-text">{review.rating}/5</span>
                        </div>
                      )}

                      <p className="review-comment">{review.comment}</p>

                      <div className="review-footer">
                        <span className="review-date">
                          {new Date(review.created_at).toLocaleDateString("en-IN", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </span>
                        <div className="review-actions">
                          <button className="btn-edit">Edit</button>
                          <button className="btn-delete">Delete</button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "purchases" && (
          <div className="profile-tab-content">
            <Transactions />
          </div>
        )}

        {activeTab === "apps" && user?.role === "developer" && (
          <div className="profile-tab-content">
            <h2>My Apps</h2>
            {apps.length === 0 ? (
              <p className="empty-message">No apps published yet</p>
            ) : (
              <div className="apps-grid">
                {apps.map((app) => (
                  <div key={app.id} className="app-card">
                    {/* Render app */}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  loaderContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh", // full page center
  },
  spinner: {
    width: "50px",
    height: "50px",
    border: "6px solid #ddd",
    borderTop: "6px solid #3498db",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
};

// Add global keyframes for spinner
const styleSheet = document.styleSheets[0];
styleSheet.insertRule(
  `@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }`,
  styleSheet.cssRules.length
);

export default Profile;